import { Box, ButtonBase, Divider, Typography } from '@mui/material';
import React, { FC } from 'react';
import { useViewport } from 'src/lib/utils';
import { useAppSelector } from 'src/store/hooks';
import colors from 'src/styles/equalution/colors';
import {
  Coupon,
  calculateDiscount,
  getCurrencySymbol,
  isUserFromAustralia,
} from 'src/lib/utils';
import { formatPricingAmount } from 'src/pages/subscription-new/utils/plans.util';
import { ORDER_SUMMARY_LINE_ITEM_WIDTH } from '../../constants';

interface ChallengePackageOrderProps {
  bundleName: string;
  billingAmount: string | number;
  originalPrice?: number;
  nextBillingAmount?: string | number;
  nextBillingDate: string;
  isPersonalCoachingAdded?: boolean;
  onRemovePersonalCoach: () => void;
  handleChangePlan: () => void;
  personalCoachPrice: number;
  coupon?: Coupon;
  intervalString?: string;
  isSistemaGiftActive?: boolean;
  renewalDescriptionLine?: string;
}

const ChallengePackageOrder: FC<ChallengePackageOrderProps> = ({
  bundleName,
  billingAmount,
  originalPrice,
  nextBillingAmount = '69',
  nextBillingDate,
  isPersonalCoachingAdded,
  personalCoachPrice,
  onRemovePersonalCoach,
  handleChangePlan,
  coupon,
  intervalString,
  isSistemaGiftActive,
  renewalDescriptionLine,
}) => {
  const { isMobile } = useViewport();
  const { isPaymentProcessing } = useAppSelector(state => state.payment);
  const { isChallengeBundleFlow } = useAppSelector(state => state.signUp);

  const originalBillingAmount = Number(billingAmount);
  const billingDiscountCalculation = calculateDiscount(
    originalBillingAmount,
    coupon
  );
  const discountedBillingAmount = billingDiscountCalculation.discountedTotal;

  const displayOriginalPrice =
    discountedBillingAmount < originalBillingAmount
      ? originalBillingAmount
      : originalPrice;

  const total =
    discountedBillingAmount +
    (isPersonalCoachingAdded ? personalCoachPrice : 0);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        paddingInline: isMobile ? '16px' : '0px',
      }}>
      {!isMobile && (
        <Box textAlign={'center'} fontSize={'20px'} fontWeight={800}>
          Order Summary
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: colors.OFF_WHITE,
          padding: '24px 16px',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}>
        <MainPlanRow
          isLoading={isPaymentProcessing}
          bundleName={bundleName}
          discountedBillingAmount={discountedBillingAmount}
          displayOriginalPrice={displayOriginalPrice}
          intervalString={intervalString}
          onChangePlan={handleChangePlan}
          coupon={coupon}
          billingDiscountCalculation={billingDiscountCalculation}
        />

        {isSistemaGiftActive && <GiftRow />}

        {isPersonalCoachingAdded && (
          <PersonalCoachAddonRow
            isLoading={isPaymentProcessing}
            personalCoachPrice={personalCoachPrice}
            onRemove={onRemovePersonalCoach}
          />
        )}

        <Divider sx={{ width: '100%', my: 0.5 }} />

        <TotalRow total={total} />

        {renewalDescriptionLine ? (
          <Typography
            sx={{
              mt: 1,
              fontSize: '14px',
              color: colors.OFF_BLACK,
              textAlign: 'left',
              width: '100%',
            }}>
            {renewalDescriptionLine}
          </Typography>
        ) : isChallengeBundleFlow ? (
          <Typography
            sx={{
              mt: 1,
              fontSize: '14px',
              color: colors.OFF_BLACK,
              textAlign: 'left',
              width: '100%',
            }}>
            Your subscription will automatically renew until cancelled.
          </Typography>
        ) : (
          <Typography
            sx={{
              mt: 1,
              fontSize: '14px',
              color: colors.OFF_BLACK,
              textAlign: 'left',
              width: '100%',
            }}>
            Then billed at {nextBillingAmount}/month from {nextBillingDate}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChallengePackageOrder;

const ActionButton = ({
  label,
  isLoading,
  onClick,
  color = colors.SALAD,
}: {
  label: string;
  onClick: () => void;
  isLoading: boolean;
  color?: string;
}) => (
  <ButtonBase disabled={isLoading} onClick={onClick}>
    <Typography
      variant="button"
      sx={{
        fontSize: '14px',
        textTransform: 'unset',
        textDecoration: 'underline',
        cursor: 'pointer',
        color: color,
        fontWeight: 600,
        textAlign: 'right',
      }}>
      {label}
    </Typography>
  </ButtonBase>
);

const MainPlanRow = ({
  bundleName,
  discountedBillingAmount,
  displayOriginalPrice,
  intervalString,
  onChangePlan,
  isLoading,
  coupon,
  billingDiscountCalculation,
}: {
  bundleName: string;
  discountedBillingAmount: number;
  displayOriginalPrice?: number;
  intervalString?: string;
  onChangePlan: () => void;
  isLoading: boolean;
  coupon?: Coupon;
  billingDiscountCalculation: any;
}) => {
  const { savings } = billingDiscountCalculation;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '2px',
          width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
          flex: 'none',
          paddingRight: '16px',
        }}>
        <Typography
          sx={{
            fontSize: '16px',
            color: colors.OFF_BLACK,
          }}>
          {bundleName}
        </Typography>
        {coupon && savings > 0 && (
          <Typography
            sx={{
              fontSize: '12px',
              color: colors.SALAD,
              fontWeight: 600,
            }}>
            You've saved {getCurrencySymbol() + formatPricingAmount(savings)}.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '2px',
          }}>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: colors.OFF_BLACK,
            }}>
            {getCurrencySymbol() + formatPricingAmount(discountedBillingAmount)}
          </Typography>
          {displayOriginalPrice &&
            Number(displayOriginalPrice) !== discountedBillingAmount && (
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  color: colors.OFF_BLACK,
                  textDecoration: 'line-through',
                }}>
                {getCurrencySymbol() +
                  formatPricingAmount(displayOriginalPrice)}
              </Typography>
            )}
          {intervalString && (
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 400,
                color: colors.OFF_BLACK,
                marginTop: '-2px',
                whiteSpace: 'nowrap',
              }}>
              {`/${intervalString}`}
            </Typography>
          )}
        </Box>
        {isUserFromAustralia() && (
          <ActionButton
            isLoading={isLoading}
            label="Change"
            onClick={onChangePlan}
          />
        )}
      </Box>
    </Box>
  );
};

const GiftRow = () => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    }}>
    <Typography
      sx={{
        fontSize: '16px',
        color: colors.OFF_BLACK,
        fontWeight: 400,
        width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
        flex: 'none',
        paddingRight: '16px',
      }}>
      Free Sistema Gift
    </Typography>

    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '2px',
        }}>
        <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: colors.OFF_BLACK,
          }}>
          {getCurrencySymbol()}0
        </Typography>
      </Box>
    </Box>
  </Box>
);

const PersonalCoachAddonRow = ({
  personalCoachPrice,
  onRemove,
  isLoading,
}: {
  personalCoachPrice: number;
  onRemove: () => void;
  isLoading: boolean;
}) => (
  <Box
    sx={{
      width: '100%',
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
    <Typography
      sx={{
        fontSize: '16px',
        color: colors.OFF_BLACK,
        width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
        flex: 'none',
        paddingRight: '16px',
      }}>
      Coaching Add-On
    </Typography>

    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
      }}>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: colors.OFF_BLACK,
        }}>
        {getCurrencySymbol() + formatPricingAmount(personalCoachPrice)}
      </Typography>
      <ActionButton
        isLoading={isLoading}
        color={colors.ERROR_RED}
        label="Remove"
        onClick={onRemove}
      />
    </Box>
  </Box>
);

const TotalRow = ({ total }: { total: number }) => (
  <Box
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
    <Typography
      sx={{
        fontSize: '20px',
        color: colors.OFF_BLACK,
        fontWeight: 800,
        width: ORDER_SUMMARY_LINE_ITEM_WIDTH,
        flex: 'none',
        paddingRight: '16px',
      }}>
      Total
    </Typography>

    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}>
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 800,
            color: colors.OFF_BLACK,
            whiteSpace: 'nowrap',
          }}>
          {getCurrencySymbol() + formatPricingAmount(total)}
        </Typography>

        <Typography
          sx={{
            fontSize: '13px',
            color: colors.OFF_BLACK,
            fontWeight: 400,
            marginTop: '-2px',
            whiteSpace: 'nowrap',
          }}>
          billed today
        </Typography>
      </Box>
    </Box>
  </Box>
);
